// ---------------------------------------------------------------------------
// What a sub-basin is missing to be calculable, per individual sub-basin —
// same criteria validarProyecto() uses to block/warn (see validations.ts),
// here as a list of human-readable reasons to flag it visually on the map
// (SubBasinMap.tsx) or the canvas (SubBasinCanvas.tsx) before the user gets
// to press "Calculate".
// ---------------------------------------------------------------------------
import type { DailySeries } from "./dailySeries.ts";
import { hasColumnAt } from "./dailySeries.ts";
import type { Translate } from "../i18n/translate.ts";
import { NO_MODEL } from "./projectEntities.ts";
import type { Aquifer, SubBasin, OutletPoint, Zone } from "./types.ts";

interface StateSeries {
  precipitation: DailySeries | null;
  etp: DailySeries | null;
  temperature: DailySeries | null;
}

interface MissingSeries {
  precipitation: boolean;
  etp: boolean;
  temperature: boolean;
}

/**
 * Which of a sub-basin's 3 AEMET-fetchable series it has no column for yet
 * — unlike `buildProblemsFor`'s reasons, NOT gated by whether a model is
 * assigned (a sub-basin with no model yet can still be missing data worth
 * fetching) and reported per-series instead of precipitation+ETP combined
 * into one reason, so AemetBulkFetchDialog.tsx can show exactly which of
 * the 3 a given sub-basin needs.
 */
export function missingSeriesFor(series: StateSeries, subBasin: SubBasin): MissingSeries {
  return {
    precipitation: !hasColumnAt(series.precipitation, subBasin.precipCol),
    etp: !hasColumnAt(series.etp, subBasin.etpCol),
    temperature: !hasColumnAt(series.temperature, subBasin.tempCol),
  };
}

interface StateCatalogs {
  zones: Zone[];
  outletPoints: OutletPoint[];
  aquifers: Aquifer[];
}

/**
 * Builds, once per set of series/catalogs (not per sub-basin), the function
 * that gives the reasons a sub-basin couldn't be calculated right now. Empty
 * list = no problems.
 *
 * Zone/outlet/aquifer are cascade-cleared (projectEntities.ts sets to null/0 any
 * sub-basin that had them assigned when deleting them), so in the normal
 * flow there should never be a dangling reference left — these checks are a
 * safety net, in case a real .ehd arrives with inconsistent data.
 */
export function buildProblemsFor(t: Translate, series: StateSeries, catalogs: StateCatalogs) {
  const existingZones = new Set(catalogs.zones.map((z) => z.zoneCode));
  const existingOutlets = new Set(catalogs.outletPoints.map((p) => p.outletCode));
  const existingAquifers = new Set(catalogs.aquifers.map((a) => a.aquiferCode));

  return (subBasin: SubBasin): string[] => {
    const reasons: string[] = [];
    if (subBasin.zoneCode == null && subBasin.snowZoneCode == null) {
      reasons.push(t("subBasinState.noZone"));
    }
    if (subBasin.zoneCode != null && !existingZones.has(subBasin.zoneCode)) {
      reasons.push(t("subBasinState.zoneDoesNotExist"));
    }
    if (subBasin.snowZoneCode != null && !existingZones.has(subBasin.snowZoneCode)) {
      reasons.push(t("subBasinState.snowZoneDoesNotExist"));
    }
    if (subBasin.outletCode == null) {
      reasons.push(t("subBasinState.noOutlet"));
    } else if (!existingOutlets.has(subBasin.outletCode)) {
      reasons.push(t("subBasinState.outletDoesNotExist"));
    }
    if (subBasin.aquiferCode !== 0 && !existingAquifers.has(subBasin.aquiferCode)) {
      reasons.push(t("subBasinState.aquiferDoesNotExist"));
    }
    if (
      subBasin.modelTypeCode !== NO_MODEL &&
      (!hasColumnAt(series.precipitation, subBasin.precipCol) ||
        !hasColumnAt(series.etp, subBasin.etpCol))
    ) {
      reasons.push(t("subBasinState.noPrecipEtpColumn"));
    }
    if (
      subBasin.snowModelTypeCode !== NO_MODEL &&
      !hasColumnAt(series.temperature, subBasin.tempCol)
    ) {
      reasons.push(t("subBasinState.noTempColumn"));
    }
    return reasons;
  };
}
